import { Component, computed, effect, ElementRef, inject, Input, OnDestroy, signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { CustomerDto, CustomerType, ErrorDto, KundenField, KundenService, RetailerDto, RetailerDtoResponse } from 'lion-api';
import { ClientConfigStore, CustomerStore, SimpleMessageService, UserStore } from '@shared/data';
import { TranslateService } from '@ngx-translate/core';

/**
 * Kunden-Selektor Component that displays a form to search for customers.
 */
@Component({
  selector: 'app-customer-include',
  templateUrl: './customer-include.component.html',
  styleUrl: './customer-include.component.scss',
  standalone: false
})
export class CustomerIncludeComponent implements OnDestroy {
  customerIncludeForm: FormGroup;
  retailerFormGroup: FormGroup;

  @ViewChild('autocompleteBank') autocompleteBank?: AutoComplete;
  @ViewChild('autocompleteRetailer') autocompleteRetailer?: AutoComplete;
  @ViewChild('focusInputElement') focusInputElement?: ElementRef<HTMLInputElement>;

  @Input() showRetailerInput = false;
  @Input() layout: 'compact' | 'standard' = 'standard';

  filteredRetailers: WritableSignal<RetailerDto[] | undefined> = signal([]);

  beauftragungswegOptions: { label: string; value: Beauftragungsweg }[];
  protected readonly KundenField = KundenField;
  protected readonly CustomerType = CustomerType;
  protected haendlerFirmenkundeDialogVisible = false;

  private customerStore = inject(CustomerStore);
  selectedKunde = this.customerStore.customer;
  selectedRetailer = this.customerStore.retailer;
  suggestedCustomers = this.customerStore.suggestedCustomers;
  hasMixedCustomerTypes = computed(() => {
    const list = this.suggestedCustomers();
    if (!list || list.length === 0) {
      return false;
    }
    const hasBank = list.some((c) => c.type === CustomerType.Bankkunde);
    const hasCorp = list.some((c) => c.type === CustomerType.Firmenkunde);
    return hasBank && hasCorp;
  });
  suggestedRetailers = this.customerStore.suggestedRetailers;
  isRefiContext = this.customerStore.isRefiContext;
  isCorporateCustomer = this.customerStore.isCorporate;
  private userStore = inject(UserStore);
  name = this.userStore.name;
  isDzBankUser = this.userStore.isDzBankUser;
  isCorporateMode = this.userStore.isCorporateMode;

  private kundenService = inject(KundenService);
  private userMainGpNumber = this.userStore.userMainGpNumber;
  private translateService = inject(TranslateService);
  private simpleMessageService = inject(SimpleMessageService);
  private readonly MESSAGE_KEY = 'customer-include';
  protected readonly messageState = this.simpleMessageService.getMessageState(this.MESSAGE_KEY);

  private clientConfigStore = inject(ClientConfigStore);
  isLicoOrigin = this.clientConfigStore.isLicoOrigin;

  constructor() {
    this.customerIncludeForm = inject(NonNullableFormBuilder).group({
      bank: [''],
      kto: [''],
      wp2: [''],
      sapgp: [''],
      murex: [''],
      customerObject: [{ value: {} as CustomerDto }],
      retailer: [{ value: {} as RetailerDto, disabled: true }]
    });

    this.retailerFormGroup = inject(NonNullableFormBuilder).group({
      name: [''],
      telefon: [''],
      handy: [''],
      beauftragungsweg: ['']
    });

    this.beauftragungswegOptions = [
      { label: this.translateService.instant('shared.customer.retailer.dialog.beauftragungswegOptions.M'), value: Beauftragungsweg.M },
      { label: this.translateService.instant('shared.customer.retailer.dialog.beauftragungswegOptions.F'), value: Beauftragungsweg.F },
      { label: this.translateService.instant('shared.customer.retailer.dialog.beauftragungswegOptions.H'), value: Beauftragungsweg.H },
      { label: this.translateService.instant('shared.customer.retailer.dialog.beauftragungswegOptions.C'), value: Beauftragungsweg.C }
    ];

    // Einmaliger Key für den LocalStorage
    const LAST_CORPORATE_CUSTOMER_KEY = 'lastCorporateCustomerSapGp';

    effect(() => {
      const customer = this.selectedKunde();
      untracked(() => {
        if (customer !== undefined) {
          console.log(`Customer changed: `, customer);

          this.customerIncludeForm.get('customerObject')?.patchValue(customer, { emitEvent: false });
          this.patchValues(customer);
          // NEU: Speichere die SAP-GP-Nummer des ausgewählten Kunden im LocalStorage
          if (this.isCorporateMode() && customer.sapGpNumber) {
            localStorage.setItem(LAST_CORPORATE_CUSTOMER_KEY, customer.sapGpNumber);
          }
          this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
          if (customer.fusioniertMit !== undefined && customer.fusioniertMit !== null) {
            this.simpleMessageService.sendInlineWarnMessage(
              this.MESSAGE_KEY,
              this.translateService.instant('administration.customer.autoComplete.fusion', {
                name: customer.fusioniertMit.name,
                sapGpNr: customer.fusioniertMit.sapGpNumber
              })
            );
            return;
          }
          if (customer.sperreLico) {
            this.simpleMessageService.sendInlineWarnMessage(
              this.MESSAGE_KEY,
              this.translateService.instant('administration.customer.autoComplete.locked')
            );
            return;
          }
          this.customerIncludeForm.get('retailer')?.enable();
        } else {
          this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
          this.autocompleteRetailer?.hide();
          this.customerIncludeForm.reset({
            bank: '',
            kto: '',
            wp2: '',
            sapgp: '',
            murex: '',
            customerObject: null,
            retailer: { value: '', disabled: true }
          });
          this.customerStore.reset();
          this.customerIncludeForm.get('retailer')?.disable();
          setTimeout(() => {
            this.autocompleteBank?.inputEL?.nativeElement.focus();
          }, 50);
        }
      });
    });

    effect(() => {
      const retailer = this.selectedRetailer();
      untracked(() => {
        this.customerIncludeForm?.patchValue({
          retailer: retailer
        });
      });
    });

    effect(() => {
      const suggestedRetailers = this.suggestedRetailers();
      this.filteredRetailers.set(suggestedRetailers.length === 0 ? undefined : suggestedRetailers);
      const retailerEnabled = this.customerIncludeForm.get('retailer')?.enabled;
      if (suggestedRetailers.length > 0 && retailerEnabled) {
        this.autocompleteRetailer?.show(true);
      }

      if (retailerEnabled) {
        this.autocompleteRetailer?.inputEL?.nativeElement.focus();
      }
    });

    effect(() => {
      const userPermissionsLoaded = this.userStore.loaded();
      untracked(() => {
        if (!userPermissionsLoaded) {
          this.disableControls();
          return;
        }

        if (this.isDzBankUser()) {
          this.enableControls();
          return;
        }

        if (this.isCorporateMode()) {
          this.enableControls();
          return;
        }

        console.log(`userMainGpNumber: ${this.userMainGpNumber()}, retailer: ${this.userStore.name()}`);
        this.customerStore.setCustomer(this.userMainGpNumber(), KundenField.Sapgpnummer);
        const retailer: RetailerDto = { type: CustomerType.Bankkunde, name: this.userStore.name() };
        this.customerStore.setRetailer(retailer);
        this.disableControls();
      });
    });

    // Effekt, um den ersten Firmenkunden als Standard zu setzen
    effect(() => {
      const corporates = this.suggestedCustomers();
      const currentCustomer = this.customerIncludeForm.get('customerObject')?.value;
      untracked(() => {
        // Dieser Effekt soll nur ausgefürt werden, wenn
        // - ein p-select verwendet wird (der aktuelle Nutzer keine DZ BANK Nutzer ist) und
        // - die Liste der Firmenkunden geladen ist und
        // - noch kein Kunde ausgewählt wurde und
        if (this.isCorporateMode() && corporates.length > 0 && (!currentCustomer || !currentCustomer.id) && !this.userStore.isDzBankUser()) {
          const lastSapGp = localStorage.getItem(LAST_CORPORATE_CUSTOMER_KEY);
          const customerToSet = corporates.find((c) => c.sapGpNumber === lastSapGp) || corporates[0];

          // Setze den Kunden im Store. Der andere `effect` wird darauf reagieren
          // und das Formular aktualisieren.
          this.customerStore.setCustomer(customerToSet, KundenField.Id);
        }
      });
    });

    this.customerIncludeForm.get('customerObject')?.valueChanges.subscribe((value) => {
      this.customerStore.setCustomer(value, KundenField.Id);
    });

    effect(() => {
      const disabled = this.customerStore.disabled();
      untracked(() => {
        if (this.userStore.isDzBankUser()) {
          if (disabled) {
            this.disableControls();
          } else {
            this.enableControls();
          }
        }
      });
    });
  }

  ngOnDestroy() {
    // Beim Verlassen der Komponente ist kein Aufräumen des LocalStorage nötig.
    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
  }

  search(event: AutoCompleteCompleteEvent) {
    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
    this.customerIncludeForm.get('retailer')?.setValue({});
    if (event.query.length < 3) {
      return;
    }

    const inputString = event.query;
    this.customerStore.search(inputString);
  }

  onSelect(event: AutoCompleteSelectEvent) {
    this.customerStore.setCustomer(event.value, KundenField.Name);
  }

  onKeyDown(formControlString: string, kundenField: KundenField) {
    const value = this.customerIncludeForm?.get(formControlString)?.value;
    if (this.customerChanged()) {
      this.customerIncludeForm.get('retailer')?.setValue({});
      this.customerStore.setCustomer(value, kundenField);
    }
  }

  onSelectRetailer(event: AutoCompleteSelectEvent) {
    setTimeout(() => {
      if (this.autocompleteRetailer) {
        this.autocompleteRetailer.hide();
      }
    }, 20);
    this.customerStore.setRetailer(event.value);
  }

  onRetailerClear() {
    this.customerStore.setRetailer(undefined);
    this.retailerFormGroup.reset();
    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
  }

  onRetailerBlur() {
    const inputValue: string | RetailerDto = this.customerIncludeForm.get('retailer')?.value;
    let retailer: RetailerDto | undefined;
    if (typeof inputValue === 'string') {
      if (this.customerStore.retailer()?.name === inputValue) {
        return;
      }

      if (this.isCorporateMode()) {
        retailer = { type: CustomerType.Firmenkunde, name: inputValue } as RetailerDto;
      } else {
        retailer = { type: CustomerType.Bankkunde, name: inputValue } as RetailerDto;
      }
    } else {
      retailer = inputValue as RetailerDto;
    }

    this.customerStore.setRetailer(retailer ?? undefined);
  }

  onClear() {
    this.customerIncludeForm.reset({
      bank: '',
      kto: '',
      wp2: '',
      sapgp: '',
      murex: '',
      customerObject: null,
      retailer: { value: '', disabled: true }
    });
    this.customerStore.reset();
    this.simpleMessageService.clearInlineMessage(this.MESSAGE_KEY);
  }

  onBankChange() {
    setTimeout(() => {
      const customerValue = this.customerIncludeForm?.get('bank')?.value;

      if (!customerValue) {
        this.onClear();
      }
    }, 200);
  }

  patchValues(customer: CustomerDto) {
    this.customerIncludeForm?.patchValue({
      bank: customer,
      kto: customer.kontonummer,
      wp2: customer.wp2Number,
      sapgp: customer.sapGpNumber,
      murex: customer.murexLabel
    });
  }

  searchRetailer($event: AutoCompleteCompleteEvent) {
    const userInput = $event.query;
    if (userInput.length !== 0) {
      const filteredRetailers = this.suggestedRetailers().filter((retailer) => retailer.name?.toLowerCase().includes(userInput.toLowerCase()));
      this.filteredRetailers.update(() => filteredRetailers);
    } else {
      const filteredRetailers = this.suggestedRetailers();
      this.filteredRetailers.update(() => filteredRetailers);
    }

    this.autocompleteRetailer?.show(true);
  }

  onRetailerFocus() {
    const suggestedRetailers = this.suggestedRetailers();
    this.filteredRetailers.update(() => suggestedRetailers);

    if (suggestedRetailers.length > 0) {
      this.autocompleteRetailer?.show(true);
    } else {
      this.autocompleteRetailer?.hide();
    }

    this.autocompleteRetailer?.inputEL?.nativeElement.focus();
  }

  saveRetailer() {
    const retailer = this.customerStore.retailer() as RetailerDto;
    const customer = this.customerStore.customer();

    if (customer && customer?.id && retailer.id && retailer.type === CustomerType.Firmenkunde) {
      retailer.name = this.retailerFormGroup.get('name')?.value;
      retailer.festnetznr = this.retailerFormGroup.get('telefon')?.value;
      retailer.mobilnr = this.retailerFormGroup.get('handy')?.value;
      retailer.beauftragungsweg = this.retailerFormGroup.get('beauftragungsweg')?.value;

      this.kundenService.updateRetailerPUT(customer.id, retailer).subscribe({
        next: (value) => {
          this.customerStore.setRetailer(value.retailer);
          this.customerIncludeForm.patchValue({
            retailer: value.retailer
          });
          this.haendlerFirmenkundeDialogVisible = false;
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('shared.customer.retailer.messages.save.success')
          );
        },
        error: (error) => {
          console.error(error);
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('shared.customer.retailer.messages.save.error')
          );
        }
      });
    } else if (customer && customer?.id && retailer.id === undefined) {
      const retailerToChange: RetailerDto = {
        type: CustomerType.Firmenkunde,
        name: this.retailerFormGroup.get('name')?.value,
        festnetznr: this.retailerFormGroup.get('telefon')?.value,
        mobilnr: this.retailerFormGroup.get('handy')?.value,
        beauftragungsweg: this.retailerFormGroup.get('beauftragungsweg')?.value
      };

      this.kundenService.updateRetailerPUT(customer?.id, retailerToChange).subscribe({
        next: (value) => {
          if (value && value.retailer) {
            this.suggestedRetailers().push(value.retailer);
          }
          this.customerStore.setRetailer(value.retailer);
          this.customerIncludeForm.patchValue({
            retailer: value.retailer
          });
          this.retailerFormGroup.reset();
          this.haendlerFirmenkundeDialogVisible = false;
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('shared.customer.retailer.messages.save.success')
          );
        },
        error: (error) => {
          console.error(error);
          this.simpleMessageService.sendInlineSuccessMessage(
            this.MESSAGE_KEY,
            this.translateService.instant('shared.customer.retailer.messages.save.error')
          );
        }
      });
    }
  }

  deleteRetailer() {
    const retailer = this.customerStore.retailer();
    const kunde = this.selectedKunde();

    if (retailer?.id && kunde?.id) {
      this.kundenService.retailerDELETE(kunde.id, retailer.id).subscribe({
        next: (value) => {
          console.log(value);
          this.customerStore.setRetailer(undefined);
          this.customerIncludeForm.patchValue({
            retailer: {}
          });
          if (kunde.id != null) {
            this.customerStore.getRetailerByKundeId(kunde.id);
          }
          this.retailerFormGroup.reset();
          this.haendlerFirmenkundeDialogVisible = false;
        },
        error: (error) => {
          console.error(error);
        }
      });
    }
  }

  protected openHaendlerFirmenkundeDialog() {
    if (!this.selectedKunde || !this.isCorporateMode) {
      return;
    }

    this.retailerFormGroup.reset();
    const retailer = this.customerStore.retailer();

    // Wenn keine retailer id gesetzt ist, prüfe anhand des Namens auf die suggested retailers.
    if (!retailer?.id) {
      const preLoaded = this.suggestedRetailers().find((preLoadedRetailer) => preLoadedRetailer.name === retailer?.name);
      if (preLoaded) {
        const retailer = preLoaded;
        this.customerStore.setRetailer(retailer);
        this.retailerFormGroup.patchValue({
          name: retailer?.name,
          telefon: retailer?.festnetznr,
          handy: retailer?.mobilnr,
          beauftragungsweg: retailer?.beauftragungsweg
        });
        this.haendlerFirmenkundeDialogVisible = true;
        return;
      }
    }

    const kunde = this.selectedKunde();
    if (retailer?.name && kunde?.id) {
      this.kundenService.searchRetailerByNameGET(kunde.id, retailer?.name).subscribe({
        next: (response: RetailerDtoResponse) => {
          if (response) {
            this.retailerFormGroup.patchValue({
              name: response.retailer?.name,
              telefon: response.retailer?.festnetznr,
              handy: response.retailer?.mobilnr,
              beauftragungsweg: response.retailer?.beauftragungsweg
            });
          } else {
            this.retailerFormGroup.patchValue({
              name: retailer?.name
            });
          }
        },
        error: (error: ErrorDto) => {
          console.error(error.message);
        }
      });
    }

    this.haendlerFirmenkundeDialogVisible = true;
  }

  private customerChanged(): boolean {
    const customer = this.customerStore.customer();
    console.log(`Customer changed 1: `, customer);
    if (customer === undefined) {
      return true;
    }

    const bankValue = this.customerIncludeForm?.get('bank')?.value;
    const bankName = typeof bankValue === 'object' && bankValue ? bankValue.name : bankValue;
    const kto = this.customerIncludeForm?.get('kto')?.value;
    const wp2 = this.customerIncludeForm?.get('wp2')?.value;
    const sapgp = this.customerIncludeForm?.get('sapgp')?.value;
    const murex = this.customerIncludeForm?.get('murex')?.value;

    if (customer?.name !== bankName) {
      return true;
    }

    if (!this.isCorporateMode() && !this.isCorporateCustomer()) {
      if (customer?.kontonummer !== kto) {
        return true;
      }
    }

    if (!this.isCorporateMode() && !this.isCorporateCustomer()) {
      if (customer.wp2Number !== wp2) {
        return true;
      }
    }

    if (customer?.sapGpNumber !== sapgp) {
      return true;
    }

    if (customer?.murexLabel !== murex) {
      return true;
    }

    return false;
  }

  private enableControls() {
    this.customerIncludeForm?.get('bank')?.enable();
    this.customerIncludeForm?.get('kto')?.enable();
    this.customerIncludeForm?.get('wp2')?.enable();
    this.customerIncludeForm?.get('sapgp')?.enable();
    this.customerIncludeForm?.get('murex')?.enable();
  }

  private disableControls() {
    this.customerIncludeForm?.get('bank')?.disable();
    this.customerIncludeForm?.get('retailer')?.disable();
    this.customerIncludeForm?.get('kto')?.disable();
    this.customerIncludeForm?.get('wp2')?.disable();
    this.customerIncludeForm?.get('sapgp')?.disable();
    this.customerIncludeForm?.get('murex')?.disable();
  }

  protected focusInput() {
    setTimeout(() => {
      if (this.focusInputElement) {
        this.focusInputElement?.nativeElement.focus();
      }
    });
  }
}

enum Beauftragungsweg {
  M = 'M',
  F = 'F',
  H = 'H',
  C = 'C'
}
